/**
 * Downloads Page
 * Professional document centre for SACCOS forms and publications.
 */
import React, { useMemo, useState } from 'react';
import { Download, FileText, Search } from 'lucide-react';

const documents = [
  {
    id: 1,
    title: 'Membership Application Form',
    description: 'Official form for new membership applications.',
    category: 'Membership Forms',
    fileUrl: '/documents/membership-application-form.pdf',
    fileType: 'PDF',
  },
  {
    id: 2,
    title: 'Loan Application Form',
    description: 'Application form for Uhamiaji SACCOS loan services.',
    category: 'Loan Forms',
    fileUrl: '/documents/loan-application-form.pdf',
    fileType: 'PDF',
  },
  {
    id: 3,
    title: 'Savings Application Form',
    description: 'Form for accessing and registering savings services.',
    category: 'Savings Forms',
    fileUrl: '/documents/savings-application-form.pdf',
    fileType: 'PDF',
  },
  {
    id: 4,
    title: 'Annual Report',
    description: 'Uhamiaji SACCOS annual report and institutional information.',
    category: 'Reports & Publications',
    fileUrl: '/documents/annual-report.pdf',
    fileType: 'PDF',
  },
];

const categories = [
  'All Categories',
  'Membership Forms',
  'Loan Forms',
  'Savings Forms',
  'Financial Documents',
  'Reports & Publications',
  'Other Documents',
];

const DownloadsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');

  const filteredDocuments = useMemo(() => {
    return documents.filter((document) => {
      const matchesSearch =
        document.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        document.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === 'All Categories' ||
        document.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  return (
    <div className="min-h-screen bg-white">
      {/* Page Header */}
      <section className="bg-[#2f3951] py-5 md:py-5">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="w-24 h-1 bg-[#ca9a37] mb-6" />
          <h1 className="text-2xl md:text-4xl font-bold text-white">
            Downloads
          </h1>
          <p className="mt-3 text-base md:text-lg text-gray-200 max-w-2xl leading-relaxed">
            Access important Uhamiaji SACCOS documents, forms, reports and publications.
          </p>
        </div>
      </section>

      {/* Downloads */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          {/* Section heading */}
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951]">
              Documents & Forms
            </h2>
            <p className="mt-2 text-gray-600">
              Find and download official Uhamiaji SACCOS documents.
            </p>
          </div>

          {/* Search and Filter */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="md:col-span-2 relative">
              <Search
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search documents..."
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ca9a37]/40 focus:border-[#ca9a37]"
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#ca9a37]/40 focus:border-[#ca9a37]"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Documents */}
          <div className="space-y-4">
            {filteredDocuments.length > 0 ? (
              filteredDocuments.map((document) => (
                <div
                  key={document.id}
                  className="group bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                    {/* File Icon */}
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#2f3951]/5 flex items-center justify-center">
                      <FileText size={28} className="text-[#2f3951]" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-semibold text-[#2f3951]">
                          {document.title}
                        </h3>
                        <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[#ca9a37]/10 text-[#8a681f]">
                          {document.fileType}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        {document.category}
                      </p>
                      <p className="mt-2 text-sm md:text-base text-gray-600 leading-relaxed">
                        {document.description}
                      </p>
                    </div>

                    {/* Download */}
                    <div className="sm:flex-shrink-0">
                      <a
                        href={document.fileUrl}
                        download
                        className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-3 rounded-xl bg-[#2f3951] text-white font-medium hover:bg-[#232b3d] transition-colors"
                      >
                        <Download size={18} />
                        Download
                      </a>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-16 border border-dashed border-gray-300 rounded-2xl">
                <FileText size={42} className="mx-auto text-gray-300" />
                <h3 className="mt-4 text-lg font-semibold text-[#2f3951]">
                  No documents found
                </h3>
                <p className="mt-2 text-gray-500">
                  Try changing your search or category.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DownloadsPage;
